// ============================================================================
// AUTH BOOTSTRAP — ensureUserProfile()
//
// Called on every sign-in to guarantee /users/{uid} exists and is readable.
//
// Behaviour:
//   1. Profile exists and readable  → return { profile, created: false }
//   2. Profile doc missing          → create as role="pending" → return { profile, created: true }
//   3. PERMISSION_DENIED            → throw { code: 'PROFILE_PERMISSION_DENIED' }
//   4. Any other Firestore error    → re-throw original error
//
// Auth invariants enforced:
//   - Self-create only allows role="pending", tenantId=null, specialtyIds=[], isActive=true
//   - Any other values must be set by tenant_admin / platform_admin via Cloud Function
//
// IMPORTANT: This module assumes Firebase has already been initialized by
// auth.firebase.js. It uses getApps()[0] to obtain the shared Firestore instance.
// ============================================================================

import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getApps } from 'firebase/app';
import { PATHS } from './firestorePaths.js';
import { selfHealIsActiveCF } from './functions.js';

// ============================================================================
// INTERNAL: get shared Firestore instance (Firebase must be initialised first)
// ============================================================================

let _db = null;
function getDb() {
  if (_db) return _db;
  const apps = getApps();
  if (!apps.length) {
    throw new Error('[authBootstrap] Firebase not initialised — call auth.firebase.js first');
  }
  _db = getFirestore(apps[0]);
  return _db;
}

// ============================================================================
// PENDING USER DOC SHAPE
// Must match Firestore rules self-CREATE conditions:
//   role="pending", tenantId=null, specialtyIds=[], isActive=true, uid=authUid
// ============================================================================

function buildPendingProfile(authUser) {
  return {
    uid:           authUser.uid,
    email:         authUser.email || '',
    displayName:   authUser.displayName || null,
    role:          'pending',
    tenantId:      null,
    specialtyIds:  [],
    isActive:      true,
    // New users enter ReferralGate in Light theme; they can switch in Settings.
    preferences:   { theme: 'light' },
    createdAt:     serverTimestamp(),
    updatedAt:     serverTimestamp(),
  };
}

// ============================================================================
// ensureUserProfile(authUser)
//
// @param {object} authUser   Firebase Auth user object ({ uid, email, displayName })
// @returns {Promise<{ profile: object, created: boolean }>}
// @throws  Error with code='PROFILE_PERMISSION_DENIED' on access error
// ============================================================================

export async function ensureUserProfile(authUser) {
  if (!authUser?.uid) {
    throw new Error('[authBootstrap] ensureUserProfile called without a valid auth user');
  }

  const db = getDb();
  const userRef = doc(db, PATHS.user(authUser.uid));

  try {
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      // Profile already exists — return it as-is
      return { profile: snap.data(), created: false };
    }

    // ---- Profile missing: create as pending --------------------------------
    const pendingData = buildPendingProfile(authUser);
    await setDoc(userRef, pendingData);

    // serverTimestamp() resolves server-side; return a client-time version
    // for immediate use in the UI (the next read will have the real timestamp).
    const localProfile = {
      ...pendingData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    return { profile: localProfile, created: true };

  } catch (err) {
    // Firestore PERMISSION_DENIED — user doc not accessible.
    // This happens when the user doc exists but lacks isActive=true (e.g. was
    // bootstrapped with status:"active" only). Attempt a one-shot self-heal via
    // the selfHealIsActive Cloud Function (Admin SDK bypasses rules), then retry.
    if (
      err?.code === 'permission-denied' ||
      err?.code === 'PERMISSION_DENIED' ||
      err?.message?.includes('permission-denied') ||
      err?.message?.includes('Missing or insufficient permissions')
    ) {
      try {
        const healResult = await selfHealIsActiveCF();
        if (healResult?.healed) {
          // isActive was stamped — retry the profile read once
          const retrySnap = await getDoc(doc(db, PATHS.user(authUser.uid)));
          if (retrySnap.exists()) {
            return { profile: retrySnap.data(), created: false };
          }
        }
      } catch (_healErr) {
        // If the heal call itself fails (e.g. Functions not deployed),
        // fall through to the standard PROFILE_PERMISSION_DENIED error.
        console.warn('[authBootstrap] selfHealIsActive failed:', _healErr?.message);
      }

      const permError = new Error(
        'Cannot load your user profile (permissions or missing user doc).'
      );
      permError.code = 'PROFILE_PERMISSION_DENIED';
      throw permError;
    }

    // Propagate any other error (network, quota, etc.)
    throw err;
  }
}

// ============================================================================
// retryEnsureUserProfile(authUser, maxAttempts, delayMs)
//
// Useful when the Cloud Function has just created the profile and a brief
// propagation delay is expected. Retries up to maxAttempts times.
//
// @param {object} authUser
// @param {number} maxAttempts  default 5
// @param {number} delayMs      initial delay in ms, doubles each retry (default 400)
// ============================================================================

export async function retryEnsureUserProfile(authUser, maxAttempts = 5, delayMs = 400) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await ensureUserProfile(authUser);
    } catch (err) {
      lastErr = err;
      // Don't retry permission errors — they won't resolve on retry
      if (err.code === 'PROFILE_PERMISSION_DENIED') throw err;
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, delayMs * attempt));
      }
    }
  }
  throw lastErr;
}

export default { ensureUserProfile, retryEnsureUserProfile };
